import { useMemo } from "react";
import { useFetcher } from "~/hooks/useFetcher";
import { conversationPlanPath, type PlanResponseWire } from "~/services/conversations";
import { planItemsToTasks, type PlanTask } from "./derivePlan";

// The SWR key for the durable plan read, or null to make no request at all.
//
// Gated on `hasPlan` (whether this conversation has a durable plan, from the
// `/connect` snapshot): the ~all conversations that never get a plan should not
// fire this GET on every open. Keying on plan existence — a property of the
// conversation, not of the current agent — also means a plan written by one
// agent still shows after a switch to a planning-off agent. Also null while a
// turn runs (the live message fold is the source then, and re-reading per
// plan-tool call would be wasteful) and when there is no conversation id yet.
export function conversationPlanKey(
  conversationId: string | undefined,
  isRunning: boolean,
  hasPlan: boolean
): string | null {
  if (!conversationId || isRunning || !hasPlan) return null;
  return conversationPlanPath(conversationId);
}

// Reads the agent's durable plan from the Postgres plan store via
// analysis-agent. Unlike the message-derived fold, the store is authoritative
// and pagination-independent: it holds the whole current plan regardless of how
// much history the client has loaded, so a long conversation whose earliest
// `write_plan` has scrolled out of the loaded window still shows the real plan.
//
// Only fetched when the conversation has a plan, and only when idle — dropping
// the SWR key to null while running and restoring it when the turn ends makes SWR
// refetch the turn's final plan exactly once, without an imperative effect (see
// `conversationPlanKey`). An unreachable store (local dev, or an outage) yields
// no data, so the caller falls back to the fold.
export function useConversationPlan(
  conversationId: string | undefined,
  isRunning: boolean,
  hasPlan: boolean
): PlanTask[] {
  const key = conversationPlanKey(conversationId, isRunning, hasPlan);
  const { data } = useFetcher<PlanResponseWire>(key);
  return useMemo(() => (data?.items ? planItemsToTasks(data.items) : []), [data]);
}
